import { useEffect, useState } from 'react'
import { Icon } from '../components/Icon'
import { StatusChip } from '../components/StatusChip'
import { useUI } from '../store/ui'
import { fullName, fmtDateTime } from '../lib/format'
import {
  listUsers,
  inviteUser,
  updateUser,
  resetUserMfa,
  resetUserPassword,
  type StaffUser,
} from '../api/admin'

const ROLE_OPTIONS = [
  { value: 'ops', label: 'Treasury / Payment Ops' },
  { value: 'admin', label: 'System Administrator' },
  { value: 'auditor', label: 'Auditor (read-only)' },
]

const COLS = 'minmax(220px,1fr) 210px 90px 150px 104px 150px'

export function Users() {
  const showToast = useUI((s) => s.showToast)
  const [users, setUsers] = useState<StaffUser[] | null>(null)
  const [err, setErr] = useState('')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [tempPw, setTempPw] = useState<{ email: string; pw: string } | null>(null)

  async function load() {
    setErr('')
    try {
      setUsers(await listUsers())
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load users')
    }
  }
  useEffect(() => {
    void load()
  }, [])

  async function changeRole(u: StaffUser, role: string) {
    try {
      await updateUser(u.id, { role })
      showToast(`Role updated for ${u.email}`, 'success')
      void load()
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Update failed', 'danger')
    }
  }

  async function toggleActive(u: StaffUser) {
    try {
      await updateUser(u.id, { active: !u.active })
      showToast(u.active ? `${u.email} suspended` : `${u.email} reactivated`, 'success')
      void load()
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Update failed', 'danger')
    }
  }

  async function resetMfa(u: StaffUser) {
    try {
      await resetUserMfa(u.id)
      showToast(`MFA reset for ${u.email} — they will re-enrol next sign-in`, 'success')
      void load()
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Reset failed', 'danger')
    }
  }

  async function resetPw(u: StaffUser) {
    try {
      const { temporary_password } = await resetUserPassword(u.id)
      setTempPw({ email: u.email, pw: temporary_password })
      void load()
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Reset failed', 'danger')
    }
  }

  return (
    <div className="page">
      <div className="ph">
        <div>
          <h1>Users &amp; roles</h1>
          <div className="desc">Console access is separate from the investment platform.</div>
        </div>
        <button className="btn primary" onClick={() => setInviteOpen(true)}>
          <Icon name="plus" size={16} /> Invite user
        </button>
      </div>

      {err && <div className="auth-error mt8">{err}</div>}

      <div className="tbl tbl-scroll">
        <div className="tr head" style={{ gridTemplateColumns: COLS }}>
          <div>Name</div>
          <div>Role</div>
          <div>MFA</div>
          <div>Last login</div>
          <div>State</div>
          <div className="cell-r">Manage</div>
        </div>

        {users === null &&
          [0, 1, 2, 3].map((i) => (
            <div className="tr body" style={{ gridTemplateColumns: COLS }} key={i}>
              <div className="skeleton" style={{ width: '70%' }} />
              <div className="skeleton" style={{ width: '80%' }} />
              <div className="skeleton" style={{ width: 40 }} />
              <div className="skeleton" style={{ width: '60%' }} />
              <div className="skeleton" style={{ width: 60 }} />
              <div />
            </div>
          ))}

        {users?.map((u) => (
          <div className="tr body" style={{ gridTemplateColumns: COLS }} key={u.id}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {fullName(u.firstname, u.lastname, u.username)}
              </div>
              <div className="mono" style={{ color: 'var(--text-3)', fontSize: 11 }}>{u.email}</div>
            </div>
            <div>
              <select className="select" value={u.roles[0] ?? ''} onChange={(e) => changeRole(u, e.target.value)}>
                {u.roles.length === 0 && <option value="">— none —</option>}
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <StatusChip status={u.mfa_enrolled ? 'Verified' : 'Incomplete'} tone={u.mfa_enrolled ? 'success' : 'warning'} />
            </div>
            <div className="mono" style={{ color: 'var(--text-2)', fontSize: 11.5 }}>{fmtDateTime(u.last_login)}</div>
            <div>
              <StatusChip status={u.active ? 'Active' : 'Suspended'} />
            </div>
            <div className="row-actions">
              <button className="btn ghost sm" title="Reset MFA" onClick={() => resetMfa(u)}>
                <Icon name="shield" size={15} />
              </button>
              <button className="btn ghost sm" title="Reset password" onClick={() => resetPw(u)}>
                <Icon name="key" size={15} />
              </button>
              <button className="btn secondary sm" onClick={() => toggleActive(u)}>
                {u.active ? 'Suspend' : 'Activate'}
              </button>
            </div>
          </div>
        ))}

        {users?.length === 0 && (
          <div className="empty">
            <div style={{ fontWeight: 600, color: 'var(--text)' }}>No users yet</div>
            <button className="btn primary mt8" onClick={() => setInviteOpen(true)}>
              Invite the first user
            </button>
          </div>
        )}
      </div>

      {inviteOpen && (
        <InviteDrawer
          onClose={() => setInviteOpen(false)}
          onInvited={(email, pw) => {
            setInviteOpen(false)
            setTempPw({ email, pw })
            void load()
          }}
        />
      )}

      {tempPw && <TempPasswordModal email={tempPw.email} pw={tempPw.pw} onClose={() => setTempPw(null)} />}
    </div>
  )
}

function InviteDrawer({ onClose, onInvited }: { onClose: () => void; onInvited: (email: string, pw: string) => void }) {
  const [email, setEmail] = useState('')
  const [firstname, setFirst] = useState('')
  const [lastname, setLast] = useState('')
  const [role, setRole] = useState('ops')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit() {
    setErr('')
    setBusy(true)
    try {
      const res = await inviteUser({ email: email.trim(), firstname, lastname, role })
      onInvited(res.user.email, res.temporary_password)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Invite failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="drawer" role="dialog" aria-label="Invite user">
        <div className="drawer-head">
          <h3>Invite user</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" size={16} />
          </button>
        </div>
        <div className="drawer-body">
          <div className="field">
            <label>Work email</label>
            <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.co.ke" />
          </div>
          <div className="two-eq">
            <div className="field">
              <label>First name</label>
              <input className="input" value={firstname} onChange={(e) => setFirst(e.target.value)} />
            </div>
            <div className="field">
              <label>Last name</label>
              <input className="input" value={lastname} onChange={(e) => setLast(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label>Role</label>
            <select className="select" value={role} onChange={(e) => setRole(e.target.value)}>
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          {err && <div className="auth-error">{err}</div>}
          <div className="auth-hint mt8">
            The user gets a one-time password and must set their own and enrol MFA on first sign-in.
          </div>
        </div>
        <div className="drawer-foot">
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy || !email} onClick={submit}>
            {busy ? 'Inviting…' : 'Send invite'}
          </button>
        </div>
      </div>
    </>
  )
}

function TempPasswordModal({ email, pw, onClose }: { email: string; pw: string; onClose: () => void }) {
  const showToast = useUI((s) => s.showToast)
  return (
    <div className="modal" role="dialog" aria-label="Temporary password">
      <div className="box">
        <div className="modal-head">
          <h3>One-time password</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" size={16} />
          </button>
        </div>
        <div className="modal-body">
          <p className="muted" style={{ marginTop: 0 }}>
            Share this one-time password with <b>{email}</b> securely. It won’t be shown again — they’ll change it on first
            sign-in.
          </p>
          <div className="copybox">
            <span style={{ flex: 1 }}>{pw}</span>
            <button
              className="btn secondary sm"
              onClick={() => {
                void navigator.clipboard?.writeText(pw)
                showToast('Copied', 'success')
              }}
            >
              Copy
            </button>
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
